'use client';

import Dialog from '@/components/Dialog';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Button } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { forwardRef, useContext } from 'react';

interface CartItemDialogProps {
  open: boolean;
  onClose: () => void;
  content?: string;
  content2?: string;
  content2TextAlignCenter?: boolean;
  confirmText?: string;
  cancelText?: string;
  removeLens?: boolean;
  onConfirm?: () => void;
  onRemoveLens?: () => void;
}

const CartItemDialog = forwardRef<HTMLDivElement, CartItemDialogProps>(
  (
    {
      open,
      onClose,
      content,
      content2,
      content2TextAlignCenter = false,
      confirmText,
      cancelText,
      removeLens = false,
      onConfirm,
      onRemoveLens,
    },
    ref,
  ) => {
    const t = useTranslations('cart.cartItemDialog');
    const { deviceType } = useContext(ResponsiveContext);
    const isPc = deviceType === EPlatform.WEB;

    // 根据平台设置弹窗宽度
    const getDialogWidth = () => {
      if (isPc && content) return '472px';
      if (isPc) return '400px';
      return '320px';
    };
    const dialogWidth = getDialogWidth();
    const handleConfirm = () => {
      onConfirm?.();
      onClose();
    };

    const handleRemoveLens = () => {
      onRemoveLens?.();
      onClose();
    };

    return (
      <Dialog
        ref={ref}
        open={open}
        onClose={onClose}
        title=""
        hideHeader
        footer={false}
        closeOnClickOverlay={false}
        sx={{
          '& .MuiDialog-paper': {
            width: dialogWidth,
            maxWidth: dialogWidth,
          },
          '& .MuiDialogContent-root': {
            padding: isPc ? '32px 32px 24px' : '24px 20px 20px',
            minHeight: isPc ? '200px' : '160px',
          },
        }}
      >
        <div className="text-center">
          {/* 主要内容 */}
          {content && (
            <p className={`text-[16px] md:text-[20px] leading-[24px] font-bs text-primary-black`}>
              {content}
            </p>
          )}

          {/* 次要内容 */}
          {content2 && (
            <p
              className={`text-[13px] md:text-[16px] text-primary-black mt-[20px] md:mt-[24px] whitespace-pre-wrap ${
                content2TextAlignCenter ? 'text-center' : ''
              }`}
            >
              {content2}
            </p>
          )}

          {/* 按钮组 */}
          <div className="flex gap-[15px] justify-center mt-[32px] md:mt-[48px]">
            <Button
              variant="outlined"
              onClick={onClose}
              className="flex-1 !w-[160px] !h-[43px] md:!h-[48px] !rounded-[50px] md:!rounded-[4px] !text-[14px] md:!text-[16px] !font-bb !border-[#808080] !text-[#282828] box-sizing-border"
              sx={{
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
              }}
            >
              {cancelText || t('no')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              className="flex-1 !w-[160px] !h-[43px] md:!h-[48px] !rounded-[50px] md:!rounded-[4px] !text-[14px] md:!text-[16px] !font-bb"
            >
              {confirmText || t('yes')}
            </Button>
          </div>

          {/* 移除镜片选项 */}
          {removeLens && (
            <p
              className="mt-4 text-sm text-primary-purple cursor-pointer"
              onClick={handleRemoveLens}
            >
              {t('removeLensOnly')}
            </p>
          )}
        </div>
      </Dialog>
    );
  },
);

CartItemDialog.displayName = 'CartItemDialog';

export default CartItemDialog;
